import React, { useState, useEffect } from 'react';
import NoticeCard from './NoticeCard';
import { Notice } from '../../types/Notice';
import api from '../../services/api';

interface NoticeListProps {
  type?: 'all' | 'central' | 'building';
  buildingId?: number;
  showActions?: boolean;
  onEdit?: (notice: Notice) => void;
  onDelete?: (notice: Notice) => void;
  onArchive?: (notice: Notice) => void;
  onPublish?: (notice: Notice) => void;
  className?: string;
  perPage?: number;
  adminMode?: boolean;
}

interface NoticeFilters {
  search: string;
  type: string;
  priority: string;
  status: string;
  urgent: boolean | null;
  dateFrom: string;
  dateTo: string;
}

const NoticeList: React.FC<NoticeListProps> = ({
  type = 'all',
  buildingId,
  showActions = false,
  onEdit,
  onDelete,
  onArchive,
  onPublish,
  className = '',
  perPage = 10,
  adminMode = false
}) => {
  const [notices, setNotices] = useState<Notice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<NoticeFilters>({
    search: '',
    type: '',
    priority: '',
    status: '',
    urgent: null,
    dateFrom: '',
    dateTo: ''
  });

  const fetchNotices = async (page = 1, searchFilters = filters) => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('per_page', perPage.toString());

      // Add filters to params
      Object.entries(searchFilters).forEach(([key, value]) => {
        if (value !== '' && value !== null && value !== undefined) {
          params.append(key, value.toString());
        }
      });

      let endpoint = '/notices';
      if (type === 'central') {
        endpoint = '/notices/central';
      } else if (type === 'building' && buildingId) {
        endpoint = `/notices/building/${buildingId}`;
      }

      // In admin mode, use the protected all-notices endpoint
      if (adminMode) {
        endpoint = '/notices/all';
      }

      const response = await api.get(`${endpoint}?${params}`);
      
      if (response.data.success) {
        setNotices(response.data.data.data);
        setTotalPages(response.data.data.last_page);
        setCurrentPage(response.data.data.current_page);
      } else {
        setError(response.data.message || 'Failed to fetch notices');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error fetching notices');
      console.error('Error fetching notices:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotices(1, filters);
  }, [type, buildingId, perPage]);

  const handleFilterChange = (key: keyof NoticeFilters, value: any) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    fetchNotices(1, newFilters);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchNotices(page, filters);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchNotices(1, filters);
  };

  const clearFilters = () => {
    const clearedFilters = {
      search: '',
      type: '',
      priority: '',
      status: '',
      urgent: null,
      dateFrom: '',
      dateTo: ''
    };
    setFilters(clearedFilters);
    fetchNotices(1, clearedFilters);
  };

  const handleNoticeAction = async (notice: Notice, action: string) => {
    try {
      // Edit is a client-side action only
      if (action === 'edit') {
        onEdit?.(notice);
        return;
      }

      let endpoint = '';
      if (action === 'archive') {
        endpoint = `/notices/${notice.id}/archive`;
      } else if (action === 'publish') {
        endpoint = `/notices/${notice.id}/publish`;
      } else if (action === 'delete') {
        endpoint = `/notices/${notice.id}`;
      }

      // Only call API if we have a valid endpoint
      if (endpoint) {
        if (action === 'delete') {
          await api.delete(endpoint);
        } else {
          await api.post(endpoint);
        }
      }

      // Refresh the list
      fetchNotices(currentPage, filters);

      // Call the parent handler if provided
      if (action === 'delete') {
        onDelete?.(notice);
      } else if (action === 'archive') {
        onArchive?.(notice);
      } else if (action === 'publish') {
        onPublish?.(notice);
      }
    } catch (err: any) {
      console.error(`Error ${action} notice:`, err);
      setError(err.response?.data?.message || `Error ${action} notice`);
    }
  };

  if (loading) {
    return (
      <div className="notice-list-loading">
        <div className="loading-spinner"></div>
        <p>Loading notices...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="notice-list-error">
        <div className="error-message">
          <h3>Error Loading Notices</h3>
          <p>{error}</p>
          <button 
            className="btn btn-primary"
            onClick={() => fetchNotices(currentPage, filters)}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`notice-list ${className}`}>
      {/* Header */}
      <div className="notice-list-header">
        <div className="list-controls">
          <h2>Notices</h2>
          <div className="view-controls">
            <button
              className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => setViewMode('grid')}
              title="Grid View"
            >
              ⊞
            </button>
            <button
              className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
              title="List View"
            >
              ☰
            </button>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="list-filters">
          <form onSubmit={handleSearch} className="search-form">
            <div className="search-input-group">
              <input
                type="text"
                placeholder="Search notices..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="search-input"
              />
              <button type="submit" className="search-btn">
                🔍
              </button>
            </div>
          </form>

          <button
            className="filter-toggle-btn"
            onClick={() => setShowFilters(!showFilters)}
          >
            🔧 Filters
          </button>
        </div>
      </div>

      {/* Advanced Filters */}
      {showFilters && (
        <div className="advanced-filters">
          <div className="filter-row">
            <div className="filter-group">
              <label>Type:</label>
              <select
                value={filters.type}
                onChange={(e) => handleFilterChange('type', e.target.value)}
              >
                <option value="">All Types</option>
                <option value="central">Central</option>
                <option value="building">Building</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Priority:</label>
              <select
                value={filters.priority}
                onChange={(e) => handleFilterChange('priority', e.target.value)}
              >
                <option value="">All Priorities</option>
                <option value="urgent">Urgent</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Status:</label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <option value="">All Status</option>
                <option value="published">Published</option>
                <option value="draft">Draft</option>
                <option value="archived">Archived</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Urgent:</label>
              <select
                value={filters.urgent === null ? '' : filters.urgent.toString()}
                onChange={(e) => handleFilterChange('urgent', e.target.value === '' ? null : e.target.value === 'true')}
              >
                <option value="">All</option>
                <option value="true">Urgent Only</option>
                <option value="false">Non-Urgent</option>
              </select>
            </div>
          </div>

          <div className="filter-row">
            <div className="filter-group">
              <label>From Date:</label>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
              />
            </div>

            <div className="filter-group">
              <label>To Date:</label>
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
              />
            </div>

            <div className="filter-actions">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={clearFilters}
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notices Grid/List */}
      {notices.length === 0 ? (
        <div className="no-notices">
          <div className="empty-state">
            <h3>No Notices Found</h3>
            <p>No notices match your current filters.</p>
            <button
              className="btn btn-primary"
              onClick={clearFilters}
            >
              Clear Filters
            </button>
          </div>
        </div>
      ) : (
        <div className={`notices-container ${viewMode}`}>
          {notices.map((notice) => (
            <NoticeCard
              key={notice.id}
              notice={notice}
              showActions={showActions}
              onEdit={() => handleNoticeAction(notice, 'edit')}
              onDelete={() => handleNoticeAction(notice, 'delete')}
              onArchive={() => handleNoticeAction(notice, 'archive')}
              onPublish={() => handleNoticeAction(notice, 'publish')}
              className={viewMode === 'list' ? 'list-item' : ''}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            ← Previous
          </button>
          
          <div className="pagination-info">
            Page {currentPage} of {totalPages}
          </div>
          
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
};

export default NoticeList;
